import { Box, GridSize, makeStyles } from '@material-ui/core';
import { CustomTheme, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useMerchantPosContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/contexts/MerchantPosContext';
import { BankAccountForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/BankAccountForm/BankAccountForm';
import { ContactDataForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/ContactDataForm/ContactDataForm';
import { DeclaredIncomeForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/DeclaredIncomeForm/DeclaredIncomeForm';
import { DocumentIdForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/DocumentIdForm';
import { FamiliarSituationForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/FamiliarSituationForm';
import { MainResidenceForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/MainResidenceForm';
import { ProfessionalDetailsForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/ProfessionalDetailsForm';
import { BorrowerOnboardingDetailsFormType } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/borrowerOnboardingDetailsForm.schema';
import { DocumentExtractorMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/DocumentExtractorDialog/maps/documentExtractor.map';
import { LastBorrowersSelector } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/LastBorrowersSelector/LastBorrowersSelector';
import { useMerchantPos } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/hooks/useMerchantPos';
import { AiButton } from '@/components/shared/components/AiButton';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import {
  DocumentExtractorProvider,
  useDocumentExtractorContext,
} from '@/components/shared/components/DocumentExtractorDialog/contexts/DocumentExtractorContext';
import { DocumentExtractorDialog } from '@/components/shared/components/DocumentExtractorDialog/DocumentExtractorDialog';
import { Font } from '@/components/shared/components/Font';
import { FormGridDirection } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { Icon } from '@/components/shared/components/Icon';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { ENV_IS_PROD } from '@/config/envVars';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { UserEntity } from '@/entities/UserEntity';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useUser } from '@/hooks/useUser';

export const BorrowerOnboardingDetailsForm = () => (
  <DocumentExtractorProvider>
    <BorrowerOnboardingDetailsFormInner />
  </DocumentExtractorProvider>
);

const BorrowerOnboardingDetailsFormInner = () => {
  const { selectedRole, onSaveDetails, update, extractedFiles, paymentMethodDetailEntity } = useMerchantPosContext();
  const requiresNieData = paymentMethodDetailEntity.requiresNieData();
  const { clearState } = useDocumentExtractorContext();

  const { sm, md } = useScreen();
  const { userEntity } = useUser();
  const { themeName } = useCustomTheme();
  const { getTextIdTranslation: t } = useTranslation();
  const { addOrUpdateBorrowerOnboardingDetails, transformFormValues } = useMerchantPos();
  const { handleSubmit, trigger, watch } = useFormContext<BorrowerOnboardingDetailsFormType>();

  const documentType = watch('documentType');

  const [extractorOpen, setExtractorOpen] = useState(false);

  useEffect(() => {
    if (!requiresNieData) {
      return;
    }

    trigger(['nationalityPeriod', 'cardId', 'expirationDateCard', 'socialSecurityDate', 'numberOfWork']);
  }, [documentType, requiresNieData, trigger]);

  if (!userEntity) {
    return null;
  }

  const showExtractor = ENV_IS_PROD !== 'true' || (themeName === CustomTheme.QUADIS && isAllowedUser(userEntity));

  const {
    state: { showLeftMenu },
  } = usePageContext();

  const onSave = handleSubmit(async (data) => {
    const borrowerOnboardingDetails = transformFormValues(data);

    if (onSaveDetails) {
      await onSaveDetails(borrowerOnboardingDetails);
    }

    await addOrUpdateBorrowerOnboardingDetails(borrowerOnboardingDetails);
    clearState();
  });

  useEffect(() => {
    update({ onSave });
  }, []);

  const gridFormProps = {
    fullWidth: false,
    fieldVariant: FieldVariant.form,
    gridProps: { sm: 6 as GridSize, md: (showLeftMenu ? 6 : 12) as GridSize },
    direction: 'row' as FormGridDirection,
  };

  const useStyles = makeStyles({
    button: {
      '& .MuiButton-label': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
    },
  });

  if (!selectedRole) {
    return null;
  }

  return (
    <CustomPaper fill="paper" style={{ padding: sm ? 24 : 8, paddingBottom: 24, position: 'relative' }}>
      <Box pt={2} pb={2} pl={3}>
        <Box>
          <Box display="flex" flexDirection={md ? 'row' : 'column-reverse'} justifyContent="space-between" gridGap={16}>
            <Box pr={3}>
              <Font variant="h4" color="title">
                <Text strong tId={selectedRole} />
              </Font>
            </Box>
            <Box mt={sm ? -2 : 0} pr={sm ? 0 : 3}>
              <Box display="flex" alignItems="center" justifyContent="flex-start" gridGap={8}>
                {showExtractor && (
                  <AiButton
                    size="small"
                    startIcon={<Icon size={16}>auto_awesome</Icon>}
                    onClick={() => setExtractorOpen(true)}
                    classes={{ root: useStyles().button }}
                  >
                    <Text tId="DOCUMENT_EXTRACTOR_BUTTON_TEXT" />
                  </AiButton>
                )}
                <LastBorrowersSelector />
              </Box>
              {showExtractor && (
                <DocumentExtractorDialog
                  opened={extractorOpen}
                  setOpened={setExtractorOpen}
                  mapValues={(results, geocoding) => DocumentExtractorMap.toFormValues(results, geocoding)}
                  onConfirmed={(confirmedEntries) => {
                    update({ extractedFiles: [...extractedFiles, ...confirmedEntries] });
                  }}
                />
              )}
            </Box>
          </Box>
          <Font variant="body2" color="textSecondary" style={{ marginTop: '6px' }}>
            <Text tId={'FILL_IN_INTERVENER_DATA'} dynamicDataTexts={[t(selectedRole).toLowerCase()]} />
          </Font>
        </Box>
      </Box>
      <Box display="flex" flexDirection={'column'} gridGap={24} pt={2} px={3}>
        <ContactDataForm {...gridFormProps} />
        <DocumentIdForm {...gridFormProps} />
        <MainResidenceForm {...gridFormProps} />
        <FamiliarSituationForm {...gridFormProps} />
        <ProfessionalDetailsForm {...gridFormProps} />
        <DeclaredIncomeForm {...gridFormProps} />
        {selectedRole === OnboardingDetailsRole.BORROWER && <BankAccountForm {...gridFormProps} />}
      </Box>
    </CustomPaper>
  );
};

const isAllowedUser = (userEntity: UserEntity) => {
  if (
    userEntity.user.username.includes('iavila') ||
    userEntity.user.username.includes('gerard.domingo') ||
    userEntity.user.username.includes('adria.moya')
  ) {
    return true;
  }
  return false;
};
